import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {DateTime} from "luxon";
import NumberFormat from "react-number-format";
import {TrashIcon} from "@heroicons/react/outline";

import BoardPopup from "../../../components/board/board-popup";
import LoaderWrapper from "../../../components/common/loader/loader-wrapper";
import InputQuantityNumber from "../../../components/common/form/input-quantity-number";
import ButtonSubmit from "../../../components/common/form/button-submit";
import UserInitial from "../../../components/common/table/user-initial";
import {classNames} from "../../../utils/Utils";
import {getFormattedDate} from "../../../utils/timeUtils";
import {
    deleteConnectionDocumentBodyEvent,
    fetchConnectionDocumentBodyEvents,
    requestConnectionDocumentBodyQuantity,
} from "../services/document-service";

const EVENT_TYPE_CONFIRMED_QTY = "CONFIRMED_QTY";
const EVENT_TYPE_REQUEST_CONFIRMED_QTY = "REQUEST_CONFIRMED_QUANTITY";
const EVENT_QUERY_TYPE_CONFIRMED_QUANTITY = "CONFIRMED_QTY";

const toNumericOrNull = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const resolveEventQuantityValue = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return 0;
    }
    const candidate = toNumericOrNull(
        eventItem.quantity
        ?? eventItem.confirmed_quantity
        ?? eventItem.qty
        ?? eventItem.value
    );
    return candidate ?? 0;
};

const getEventType = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return "";
    }
    const raw = eventItem.type ?? eventItem.event_type ?? eventItem.eventType ?? "";
    const normalized = typeof raw === "string" ? raw.toUpperCase() : "";
    if (normalized === "CONFIRMED_QUANTITY") {
        return EVENT_TYPE_CONFIRMED_QTY;
    }
    return normalized;
};

const normalizeEventDate = (value) => {
    if (!value) {
        return "";
    }

    let parsed = DateTime.fromSQL(value);
    if (!parsed.isValid) {
        parsed = DateTime.fromISO(value);
    }
    if (!parsed.isValid) {
        parsed = DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss");
    }
    if (!parsed.isValid) {
        return "";
    }
    return parsed.toFormat("yyyy-MM-dd HH:mm:ss");
};

const extractEventsArray = (payload) => {
    if (!payload) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.events)) {
        return payload.events;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    if (Array.isArray(payload?.items)) {
        return payload.items;
    }
    return [];
};

const extractEventUserCandidate = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return null;
    }
    const candidates = [
        eventItem.user,
        eventItem.creator,
        eventItem.created_by,
        eventItem.createdBy,
    ];
    const header = eventItem.document_header ?? eventItem.documentHeader ?? null;
    if (header) {
        candidates.push(header.creator);
        if (Array.isArray(header.users) && header.users.length > 0) {
            candidates.push(header.users[0]);
        }
    }
    return candidates.find((candidate) => candidate && typeof candidate === "object") ?? null;
};

const normalizeEventUser = (userCandidate) => {
    if (!userCandidate || typeof userCandidate !== "object") {
        return null;
    }
    const firstName = userCandidate.firstname ?? userCandidate.first_name ?? userCandidate.firstName ?? "";
    const lastName = userCandidate.lastname ?? userCandidate.last_name ?? userCandidate.lastName ?? "";
    const fallbackFullname = [firstName, lastName].filter(Boolean).join(" ");
    const fullname = userCandidate.fullname
        ?? fallbackFullname
        ?? userCandidate.name
        ?? userCandidate.username
        ?? userCandidate.email
        ?? "";
    return {
        id: userCandidate.id ?? userCandidate.uuid ?? null,
        fullname,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        firstname: firstName || undefined,
        lastname: lastName || undefined,
        color: userCandidate.color ?? null,
        picture: userCandidate.picture ?? null,
    };
};

const sanitizeEventItem = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return null;
    }
    const type = getEventType(eventItem);
    if (!type) {
        return null;
    }
    const eventId = eventItem.id ?? eventItem.event_id ?? eventItem.eventId ?? eventItem.uuid ?? null;
    const userCandidate = extractEventUserCandidate(eventItem);
    const normalizedUser = normalizeEventUser(userCandidate);
    const rawEventDate = eventItem.event_date
        ?? eventItem.eventDate
        ?? eventItem.created_at
        ?? eventItem.createdAt
        ?? null;
    const normalizedEventDate = normalizeEventDate(rawEventDate) || getCurrentServerDateTime();
    const unitSymbol = eventItem.unit_symbol
        ?? eventItem.unitSymbol
        ?? eventItem.unit?.symbol
        ?? eventItem.unit_value?.symbol
        ?? "";

    return {
        id: eventId ?? `event-${Date.now()}`,
        type,
        quantity: resolveEventQuantityValue(eventItem),
        event_date: normalizedEventDate,
        unit_symbol: unitSymbol,
        user: normalizedUser,
        status: eventItem.status ?? null,
        closed: typeof eventItem.closed === "boolean" ? eventItem.closed : null,
    };
};

const summarizeEventsList = (list) => {
    const events = Array.isArray(list) ? list : [];
    const summary = {
        events,
        total: events.length,
        confirmedEvents: [],
        confirmedCount: 0,
        confirmedQuantityTotal: 0,
        requestedEvents: [],
        requestedCount: 0,
    };

    events.forEach((eventItem) => {
        const type = getEventType(eventItem);
        if (type === EVENT_TYPE_CONFIRMED_QTY) {
            summary.confirmedEvents.push(eventItem);
            summary.confirmedCount += 1;
            summary.confirmedQuantityTotal += resolveEventQuantityValue(eventItem);
        } else if (type === EVENT_TYPE_REQUEST_CONFIRMED_QTY) {
            summary.requestedEvents.push(eventItem);
            summary.requestedCount += 1;
        }
    });

    return summary;
};

const getCurrentServerDateTime = () => (
    DateTime.local().toFormat("yyyy-MM-dd HH:mm:ss")
);

const isApprovedStatus = (eventItem) => {
    const statusValue = eventItem?.status;
    if (!statusValue || typeof statusValue !== "string") {
        return false;
    }
    return statusValue.toUpperCase() === "APPROVED";
};

const isConfirmedStatus = (eventItem) => {
    const statusValue = eventItem?.status;
    if (!statusValue || typeof statusValue !== "string") {
        return false;
    }
    return statusValue.toUpperCase() === "CONFIRMED";
};

const ConnectionDocumentBodyConfirmedQuantityModal = ({
    isOpen,
    onClose,
    companyId,
    documentId,
    bodyId,
    onEventsChange,
    rowQuantity,
    initialSummary,
}) => {
    const {t} = useTranslation();
    const [events, setEvents] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [requestQuantity, setRequestQuantity] = useState("");
    const [rowLoading, setRowLoading] = useState({});
    const [activeTab, setActiveTab] = useState("confirmed");
    const [reloadKey, setReloadKey] = useState(0);
    const wasOpenRef = useRef(false);
    const onEventsChangeRef = useRef(onEventsChange);

    useEffect(() => {
        onEventsChangeRef.current = onEventsChange;
    }, [onEventsChange]);

    const notifyParent = useCallback((summary) => {
        const handler = onEventsChangeRef.current;
        if (typeof handler === "function") {
            handler(summary);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            wasOpenRef.current = false;
            return;
        }

        setRequestQuantity("");
        const defaultEvents = Array.isArray(initialSummary?.events) ? initialSummary.events : [];
        const sanitizedDefaults = defaultEvents
            .map((entry) => sanitizeEventItem(entry))
            .filter(Boolean);
        setEvents(sanitizedDefaults);
        if (!wasOpenRef.current) {
            const hasRequestedOnly = (initialSummary?.requestedCount ?? 0) > 0
                && (initialSummary?.confirmedCount ?? 0) === 0;
            setActiveTab(hasRequestedOnly ? "requested" : "confirmed");
        }
        wasOpenRef.current = true;
    }, [initialSummary, isOpen]);

    useEffect(() => {
        if (!isOpen || !companyId || !documentId || !bodyId) {
            return undefined;
        }

        let isSubscribed = true;

        const loadEventsFromApi = async () => {
            setIsFetching(true);
            try {
                const queryTypes = [
                    EVENT_QUERY_TYPE_CONFIRMED_QUANTITY,
                    EVENT_TYPE_REQUEST_CONFIRMED_QTY,
                ];
                const responses = await Promise.allSettled(
                    queryTypes.map((typeValue) => fetchConnectionDocumentBodyEvents({
                        companyId,
                        documentId,
                        bodyId,
                        params: {type: typeValue},
                    }))
                );

                if (!isSubscribed) {
                    return;
                }

                const aggregatedEvents = [];
                let errorMessage = null;

                responses.forEach((result) => {
                    if (result.status === "fulfilled") {
                        aggregatedEvents.push(...extractEventsArray(result.value));
                        return;
                    }
                    if (!errorMessage) {
                        errorMessage = result.reason?.message
                            ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"});
                    }
                });

                const sanitizedFetchedEvents = aggregatedEvents
                    .map((entry) => sanitizeEventItem(entry))
                    .filter(Boolean);

                const summaryResult = summarizeEventsList(sanitizedFetchedEvents);
                setEvents(sanitizedFetchedEvents);
                notifyParent(summaryResult);

                if (errorMessage) {
                    toast.error(errorMessage);
                }
            } catch (error) {
                if (!isSubscribed) {
                    return;
                }
                toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
            } finally {
                if (isSubscribed) {
                    setIsFetching(false);
                }
            }
        };

        loadEventsFromApi();

        return () => {
            isSubscribed = false;
        };
    }, [bodyId, companyId, documentId, isOpen, notifyParent, reloadKey, t]);

    const handleClose = useCallback(() => {
        onClose();
        setEvents([]);
        setRequestQuantity("");
        setRowLoading({});
        setActiveTab("confirmed");
        setIsFetching(false);
    }, [onClose]);

    const handleSubmitRequest = useCallback(async (event) => {
        event.preventDefault();
        const quantityValue = toNumericOrNull(requestQuantity);

        if (quantityValue === null || quantityValue <= 0) {
            toast.error(t("connection.document.confirmed_quantity.request_invalid", {defaultValue: "Inserisci una quantità valida da richiedere"}));
            return;
        }

        if (!companyId || !documentId || !bodyId) {
            toast.error(t("connection.document.confirmed_quantity.request_missing_params", {defaultValue: "Parametri mancanti per inviare la richiesta"}));
            return;
        }

        setIsSubmitting(true);
        try {
            await requestConnectionDocumentBodyQuantity({
                companyId,
                documentId,
                bodyId,
                quantity: quantityValue,
                type: EVENT_TYPE_REQUEST_CONFIRMED_QTY,
            });
            setActiveTab("requested");
            setReloadKey((prev) => prev + 1);

            toast.success(t("connection.document.confirmed_quantity.request_success", {defaultValue: "Richiesta inviata"}));
            setRequestQuantity("");
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setIsSubmitting(false);
        }
    }, [bodyId, companyId, documentId, requestQuantity, t]);

    const handleDeleteEvent = useCallback(async (eventId) => {
        if (!eventId || !companyId || !documentId || !bodyId) {
            return;
        }

        setRowLoading((prev) => ({...prev, [eventId]: true}));
        try {
            await deleteConnectionDocumentBodyEvent({
                companyId,
                documentId,
                bodyId,
                eventId,
            });
            toast.success(t("connection.document.confirmed_quantity.event_deleted", {defaultValue: "Evento eliminato"}));
            setReloadKey((prev) => prev + 1);
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setRowLoading((prev) => {
                const next = {...prev};
                delete next[eventId];
                return next;
            });
        }
    }, [bodyId, companyId, documentId, t]);

    const eventsSummary = useMemo(() => summarizeEventsList(events), [events]);
    const confirmedEvents = eventsSummary.confirmedEvents;
    const requestedEvents = eventsSummary.requestedEvents;

    const tabs = useMemo(() => ([
        {
            id: "confirmed",
            name: t("connection.document.confirmed_quantity.tab_confirmed", {defaultValue: "Confermate"}),
            count: eventsSummary.confirmedCount,
        },
        {
            id: "requested",
            name: t("connection.document.confirmed_quantity.tab_requested", {defaultValue: "Richieste"}),
            count: eventsSummary.requestedCount,
        },
    ]), [eventsSummary.confirmedCount, eventsSummary.requestedCount, t]);

    const renderEventList = (list, emptyMessage, allowDelete = true) => {
        if (!list || list.length === 0) {
            return (
                <p className="mt-3 text-sm text-gray-500">
                    {emptyMessage}
                </p>
            );
        }

        return (
            <ul className="mt-4 max-h-64 divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200 bg-white">
                {list.map((eventItem, index) => {
                    const eventId = eventItem?.id ?? eventItem?.event_id ?? eventItem?.uuid ?? `event-${index}`;
                    const eventUser = eventItem?.user ?? eventItem?.creator ?? eventItem?.created_by ?? null;
                    const userFullname = eventUser
                        ? (
                            eventUser.fullname
                            ?? [eventUser.first_name, eventUser.last_name].filter(Boolean).join(" ")
                            ?? eventUser.name
                            ?? eventUser.username
                            ?? eventUser.email
                        )
                        : null;
                    const normalizedDate = normalizeEventDate(
                        eventItem?.event_date
                        ?? eventItem?.eventDate
                        ?? eventItem?.created_at
                        ?? eventItem?.createdAt
                    );
                    const eventDate = normalizedDate ? getFormattedDate(normalizedDate) : "-";
                    const quantityValue = resolveEventQuantityValue(eventItem);
                    const unitSymbol = eventItem?.unit_symbol
                        ?? eventItem?.unitSymbol
                        ?? eventItem?.unit?.symbol
                        ?? eventItem?.unit_value?.symbol
                        ?? "";
                    const fallbackUserName = userFullname || t("app.common.unknown_user", {defaultValue: "Utente sconosciuto"});
                    const userInitialData = eventUser ?? {fullname: fallbackUserName};
                    const canDelete = allowDelete && eventId && !isConfirmedStatus(eventItem);

                    return (
                        <li key={eventId} className="px-4 py-3 text-sm lg:flex lg:items-center lg:justify-between">
                            <div className="flex flex-1 items-center">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <UserInitial user={userInitialData} />
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-gray-900">{fallbackUserName}</p>
                                        <p className="text-xs text-gray-500">{eventDate}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-right lg:mt-0">
                                <NumberFormat
                                    value={quantityValue}
                                    displayType="text"
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={4}
                                    suffix={unitSymbol ? ` ${unitSymbol}` : ""}
                                    renderText={(text) => (
                                        <span className="text-sm font-semibold text-gray-900">{text}</span>
                                    )}
                                />
                                {isApprovedStatus(eventItem) && (
                                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-700">
                                        {approvedStatusLabel}
                                    </span>
                                )}
                                {canDelete && (
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteEvent(eventId)}
                                        disabled={Boolean(rowLoading[eventId])}
                                        className="inline-flex items-center justify-center rounded-md border border-transparent p-1 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                        aria-label={t("app.common.delete", {defaultValue: "Elimina"})}
                                    >
                                        {rowLoading[eventId] ? (
                                            t("app.common.loading", {defaultValue: "Caricamento"})
                                        ) : (
                                            <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const requestedFormLabel = useMemo(() => (
        t("connection.document.confirmed_quantity.request_label", {defaultValue: "Quantità da richiedere"})
    ), [t]);

    const requestButtonLabel = useMemo(() => (
        t("connection.document.confirmed_quantity.request_action", {defaultValue: "Invia richiesta"})
    ), [t]);

    const approvedStatusLabel = useMemo(() => (
        t("connection.document.confirmed_quantity.status_approved", {defaultValue: "Approved"})
    ), [t]);

    const confirmedEmptyMessage = useMemo(() => (
        t("connection.document.confirmed_quantity.empty_confirmed", {defaultValue: "Non sono presenti quantità confermate"})
    ), [t]);

    const requestedEmptyMessage = useMemo(() => (
        t("connection.document.confirmed_quantity.empty_requested", {defaultValue: "Non sono presenti richieste registrate"})
    ), [t]);

    return (
        <BoardPopup
            isOpen={isOpen}
            setIsOpen={(status) => {
                if (!status) {
                    handleClose();
                }
            }}
            canClose
            setClean={() => {}}
        >
            <div className="-mt-4 mb-3 bg-white">
                <div className="px-4 sm:px-6">
                    <div className="flex items-center justify-between mt-2 mb-6">
                        <div className="flex items-start space-x-5">
                            <div className="pt-1.5">
                                <h1 className="text-xl font-bold text-blue-1000">
                                    {t("connection.document.confirmed_quantity.title", {defaultValue: "Quantità confermate"})}
                                </h1>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t("connection.document.confirmed_quantity.subtitle", {defaultValue: "Consulta le quantità confermate e invia richieste di conferma."})}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <nav className="-mb-px flex space-x-4" aria-label="Tabs">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setActiveTab(tab.id)}
                                        className={classNames(
                                            tab.id === activeTab
                                                ? "border-blue-500 text-blue-600"
                                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                                            "whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium"
                                        )}
                                    >
                                        <span>{tab.name}</span>
                                        <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                                            {tab.count}
                                        </span>
                                    </button>
                                ))}
                            </nav>
                        </div>

                        {activeTab === "requested" && (
                            <form onSubmit={handleSubmitRequest} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                                    <InputQuantityNumber
                                        id={`connection-request-qty-${bodyId}`}
                                        label={requestedFormLabel}
                                        value={requestQuantity}
                                        onChange={(value) => setRequestQuantity(value)}
                                        isFullW
                                        isDisabled={isFetching || isSubmitting}
                                    />
                                    <div className="flex justify-end">
                                        <ButtonSubmit
                                            isLoading={isSubmitting}
                                            label={requestButtonLabel}
                                            isFullWith={false}
                                            disabled={isFetching}
                                        />
                                    </div>
                                </div>
                            </form>
                        )}

                        <LoaderWrapper isLoading={isFetching}>
                            {activeTab === "confirmed"
                                ? renderEventList(confirmedEvents, confirmedEmptyMessage, false)
                                : renderEventList(requestedEvents, requestedEmptyMessage, true)}
                        </LoaderWrapper>
                    </div>
                </div>
            </div>
        </BoardPopup>
    );
};

ConnectionDocumentBodyConfirmedQuantityModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bodyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEventsChange: PropTypes.func,
    rowQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialSummary: PropTypes.shape({
        events: PropTypes.arrayOf(PropTypes.object),
        confirmedCount: PropTypes.number,
        confirmedQuantityTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        requestedCount: PropTypes.number,
    }),
};

ConnectionDocumentBodyConfirmedQuantityModal.defaultProps = {
    isOpen: false,
    companyId: null,
    documentId: null,
    bodyId: null,
    onEventsChange: undefined,
    rowQuantity: "",
    initialSummary: null,
};

export default ConnectionDocumentBodyConfirmedQuantityModal;
